import { useState } from 'react';
import { db } from '../db/client';
import { createOrganization } from '../db/auth';

interface Props {
  userId: string;
  userEmail: string;
  onPick: (orgId: string) => void;
}

export function OrgPickerPanel({ userId, userEmail, onPick }: Props) {
  const { isLoading, data, error } = db.useQuery({
    organizations: {
      $: { where: { 'memberships.user.id': userId }, order: { createdAt: 'desc' as const } },
    },
  });

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  if (isLoading) return <div className="uin-muted">Loading orgs…</div>;
  if (error) return <div className="uin-error">{error.message}</div>;

  const orgs = data?.organizations ?? [];

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setErrMsg(null);
    try {
      const id = await createOrganization(name.trim(), userId, userEmail);
      onPick(id);
    } catch (err) {
      setErrMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="uin-form">
      <h3>Pick an organization</h3>

      {orgs.length === 0 && !creating && (
        <p className="uin-muted">You don't belong to any organization yet. Create one to start.</p>
      )}

      {orgs.map((org) => (
        <button key={org.id} className="uin-org-row" onClick={() => onPick(org.id)}>
          <span style={{ fontWeight: 500 }}>{org.name}</span>
          <span className="uin-muted">→</span>
        </button>
      ))}

      {errMsg && <div className="uin-error">{errMsg}</div>}

      {creating ? (
        <form onSubmit={handleCreate} style={{ marginTop: 12 }}>
          <label className="uin-label">Organization name</label>
          <input
            autoFocus
            className="uin-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Inc."
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="uin-btn-primary" type="submit" disabled={busy || !name.trim()}>
              {busy ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              className="uin-btn-secondary"
              onClick={() => {
                setCreating(false);
                setName('');
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          className="uin-btn-primary"
          style={{ marginTop: 12 }}
          onClick={() => setCreating(true)}
        >
          + Create organization
        </button>
      )}
    </div>
  );
}
